// Presentation-only helpers for the Campaign Center. NO business logic / mutations —
// pure derivations over data already returned by existing PPE + Social APIs.

const UNIT_MS = {
  minutes: 60e3, hours: 3600e3, days: 86400e3, weeks: 604800e3,
  months: 2592000e3, years: 31536000e3,
};

export function intervalMs(value, unit) {
  return (UNIT_MS[unit] || UNIT_MS.hours) * Math.max(1, Number(value) || 1);
}

// A post in one of these states will not be published again: it counts as DONE in the progress
// (fix 02/11/2026: failed/partial posts used to stay "remaining" forever -> never 100%).
export const DONE_POST_STATUSES = ["published", "partial", "failed"];
export const isPostDone = (post) => !!post && DONE_POST_STATUSES.includes(post.status);
export const isPostError = (post) => !!post && (post.status === "failed" || post.status === "partial");

// Derives progress from promotion_items + linked social posts (by id). Only the CURRENT run counts.
export function computeProgress(items = [], postsById = {}, runSeq = null) {
  const cur = runSeq ? items.filter((it) => Number(it.run_seq) === Number(runSeq)) : items;
  const products = {};
  let publishedItems = 0, failedItems = 0, doneItems = 0;
  cur.forEach((it) => {
    const post = postsById[it.social_post_id];
    const done = isPostDone(post) || it.status === "failed";
    if (post && post.status === "published") publishedItems += 1;
    if (isPostError(post) || it.status === "failed") failedItems += 1;
    if (done) doneItems += 1;
    (products[it.product_external_id] ||= []).push({ done });
  });
  const totalProducts = Object.keys(products).length;
  const publishedProducts = Object.values(products).filter((arr) => arr.every((x) => x.done)).length;
  const remainingProducts = Math.max(0, totalProducts - publishedProducts);
  const totalItems = cur.length;
  const pct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;
  return { totalItems, publishedItems, failedItems, doneItems, totalProducts, publishedProducts, remainingProducts, pct };
}

// Card/table progress: the server's real summary (promo.progress) when present.
export function progressFromPromo(promo) {
  const p = promo?.progress;
  if (p && p.posts_total) {
    return { total: p.products_total, processed: p.products_done, remaining: p.products_remaining,
             pct: p.pct, errors: p.errors, posts: p.posts_total, postsDone: p.posts_done,
             nextPublishAt: p.next_publish_at, lastActivity: p.last_published_at, groups: p.groups, real: true };
  }
  return null;
}

// Estimated completion using only available data (interval + planned times).
export function estimateCompletion(promo, items = [], postsById = {}) {
  const now = Date.now();
  const pending = items.filter((it) => !isPostDone(postsById[it.social_post_id]) && it.status !== "failed");
  const plannedFuture = pending
    .map((it) => new Date(it.planned_publish_at).getTime())
    .filter((t) => !Number.isNaN(t));
  const nextTs = plannedFuture.filter((t) => t >= now).sort((a, b) => a - b)[0]
    || (promo?.next_run_at ? new Date(promo.next_run_at).getTime() : null);
  const endTs = plannedFuture.length ? Math.max(...plannedFuture) : null;
  const etaMs = endTs ? Math.max(0, endTs - now) : null;
  return {
    nextPublishAt: nextTs ? new Date(nextTs).toISOString() : null,
    endDate: endTs ? new Date(endTs).toISOString() : null,
    etaMs,
    remaining: pending.length,
  };
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export function fmtDuration(ms) {
  if (ms == null) return "—";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), rm = m % 60;
  if (h < 24) return `${h}h ${rm}m`;
  const d = Math.floor(h / 24), rh = h % 24;
  return `${d}d ${rh}h`;
}

export function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

// Lightweight per-campaign estimate from the promotion doc alone (no items fetch).
export function estimateFromPromo(promo) {
  const real = progressFromPromo(promo);
  if (real) {
    const nextTs = real.nextPublishAt ? new Date(real.nextPublishAt).getTime() : null;
    const iv = intervalMs(promo.interval_value, promo.interval_unit);
    const endTs = nextTs && real.remaining > 0 ? nextTs + (real.remaining - 1) * iv : null;
    return { ...real, nextPublishAt: real.nextPublishAt || promo.next_run_at || null,
             endDate: endTs ? new Date(endTs).toISOString() : null,
             etaMs: endTs ? Math.max(0, endTs - Date.now()) : null };
  }
  const total = Number(promo.max_items) || (promo.counters?.selected || 0);
  const processed = promo.counters?.selected || 0;
  const remaining = Math.max(0, total - processed);
  const pct = total ? Math.round((processed / total) * 100) : 0;
  const nextTs = promo.next_run_at ? new Date(promo.next_run_at).getTime() : null;
  const iv = intervalMs(promo.interval_value, promo.interval_unit);
  const endTs = nextTs && remaining > 0 ? nextTs + (remaining - 1) * iv : nextTs;
  const etaMs = endTs ? Math.max(0, endTs - Date.now()) : null;
  return {
    total, processed, remaining, pct,
    nextPublishAt: nextTs ? new Date(nextTs).toISOString() : null,
    endDate: endTs ? new Date(endTs).toISOString() : null,
    etaMs,
  };
}

export function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const suffix = diff >= 0 ? "fa" : "";
  const prefix = diff < 0 ? "tra " : "";
  const m = Math.round(abs / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${prefix}${m}m ${suffix}`.trim();
  const h = Math.round(m / 60);
  if (h < 24) return `${prefix}${h}h ${suffix}`.trim();
  const d = Math.round(h / 24);
  return `${prefix}${d}g ${suffix}`.trim();
}

// Normalize a product's images into an ordered list of URL STRINGS.
// Prefers the already-normalized DTO field `image_urls` (strings from
// sources._images), then falls back to `raw.images`, which for Etsy are OBJECTS
// ({ url_fullxfull, url_570xN, ... }) — unwrap them to a URL string.
export function productImages(product) {
  if (!product) return [];
  if (Array.isArray(product.image_urls) && product.image_urls.length) return product.image_urls;
  const raw = product.raw || {};
  const toUrl = (x) =>
    typeof x === "string" ? x : x?.url_fullxfull || x?.url_570xN || x?.url || x?.src || null;
  return (raw.images || []).map(toUrl).filter(Boolean);
}
